import React, { useEffect, useState, useRef } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { useSite } from '../context/SiteContext';

import { PreviewCtx, DragCtx, CtxMenuCtx } from './eventPage/contexts/index';
import { Section } from './eventPage/canvas/Section';
import { FooterSection } from './eventPage/canvas/FooterSection';

const NOOP = () => {};

function makePreviewDragCtx(breakpointRef) {
  return {
    drag:          { current: null },
    activeDropId:  { current: null },
    sectionDrag:   { current: null },
    breakpointRef,
    _resets:       new Set(),
    resetAll:      NOOP,
  };
}

const NO_CTX_MENU = { open: NOOP };

// ── Responsive breakpoint hook ────────────────────────────────────────────────
function useBreakpoint() {
  const get = () => window.innerWidth <= 480 ? 'mobile' : window.innerWidth <= 900 ? 'tablet' : 'desktop';
  const [bp, setBp] = useState(get);
  useEffect(() => {
    const h = () => setBp(get());
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return bp;
}

// ── Config resolution ─────────────────────────────────────────────────────────
export function resolveHeaderConfig(page, eventLayout) {
  if (page?.headerOverride) {
    const s = page.sections?.find(s => s.type === 'header');
    return s?.headerConfig || eventLayout?.header || {};
  }
  return eventLayout?.header || {};
}

function resolveFooterConfig(page, eventLayout) {
  if (page?.footerOverride) {
    const s = page.sections?.find(s => s.type === 'footer');
    return s?.footerConfig || eventLayout?.footer || {};
  }
  return eventLayout?.footer || {};
}

// ── Builder preview ───────────────────────────────────────────────────────────
// Header is now rendered in PublicPage shell so it stays mounted across
// page navigations and benefits from the global navbar slot.
// BuilderPreview only renders content sections + footer.
const BuilderPreview = ({ page, eventLayout, bp, stickyHeaderHeight }) => {
  const breakpointRef = useRef(bp);
  breakpointRef.current = bp;

  const dragCtx = useRef(makePreviewDragCtx(breakpointRef)).current;
  const ctxMenu = useRef(NO_CTX_MENU).current;

  const sections = page?.sections || [];
  if (sections.length === 0) return null;

  const footerSection   = sections.find(s => s.type === 'footer');
  const contentSections = sections.filter(s => s.type !== 'header' && s.type !== 'footer');

  const footerConfig = resolveFooterConfig(page, eventLayout);

  return (
    <PreviewCtx.Provider value={true}>
      <CtxMenuCtx.Provider value={ctxMenu}>
        <DragCtx.Provider value={dragCtx}>

          {/* Content sections */}
          {contentSections.map((section, idx) => (
            <Section
              key={section.id}
              section={section}
              sectionIdx={idx}
              sectionsCount={contentSections.length}
              stickyHeaderHeight={idx === 0 ? stickyHeaderHeight : 0}
              selectedId={null}
              selectedSectionId={null}
              onSelectSection={NOOP}
              onSelect={NOOP}
              onChange={NOOP}
              onDelete={NOOP}
              onDrop={NOOP}
              onSelectChild={NOOP}
              onReorder={NOOP}
              onSectionReorder={NOOP}
              onSectionDuplicate={NOOP}
              onSectionCopy={NOOP}
              onSectionPaste={NOOP}
              sectionClipboard={null}
              onUpdateEl={NOOP}
              onDeleteEl={NOOP}
              onMoveChildToSection={NOOP}
              onMoveChildToContainer={NOOP}
              onInsertNewChildAt={NOOP}
              onMoveTopLevelIntoContainer={NOOP}
              onRename={NOOP}
              onMergeAsRow={NOOP}
              onSplitDropIntoEmpty={NOOP}
            />
          ))}

          {/* Footer — render when the page has a footer section (override) or a global footer config exists */}
          {(footerSection || Object.keys(footerConfig).length > 0) && (
            <FooterSection
              section={footerSection || { id: 'shell-footer', type: 'footer' }}
              config={footerConfig}
              selected={false}
              onSelectFooter={NOOP}
              breakpoint={bp}
            />
          )}

        </DragCtx.Provider>
      </CtxMenuCtx.Provider>
    </PreviewCtx.Provider>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const EventPage = ({ preloadedEvent, preloadedOrganization, preloadedForms, preloadedPageSlug } = {}) => {
  const { setEventHeader } = useSite();
  const location = useLocation();
  const [pagesLoading, setPagesLoading]     = useState(true);
  const [publishedPages, setPublishedPages] = useState(null);
  const bp = useBreakpoint();

  const event       = preloadedEvent || null;
  const branding    = event?.branding || {};
  const eventLayout = event?.layout   || {};
  const activeSlug  = preloadedPageSlug || null;

  useEffect(() => {
    if (!event?._id) { setPagesLoading(false); return; }
    setPagesLoading(true);
    api.get(`/events/${event._id}/pages/public`)
      .then(res => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          const pub = res.data.data
            .filter(p => p.published)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          setPublishedPages(pub.length ? pub : []);
        } else {
          setPublishedPages([]);
        }
      })
      .catch(() => setPublishedPages([]))
      .finally(() => setPagesLoading(false));
  }, [event?._id]);

  // Derive the active page
  const activePage = !publishedPages ? null
    : !activeSlug
      ? (publishedPages.find(p => p.isDefault) || publishedPages[0])
      : publishedPages.find(p => p.slug === activeSlug);

  // Update favicon whenever the event branding changes
  useEffect(() => {
    if (event?.branding?.logoUrl) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = event.branding.logoUrl;
    }
    return () => {
      const link = document.querySelector("link[rel='icon']");
      if (link) link.href = '/favicon.ico';
    };
  }, [event?.branding?.logoUrl]);

  // Update document title whenever the active page changes
  useEffect(() => {
    if (activePage?.title && event?.name) {
      document.title = `${activePage.title} | ${event.name}`;
    } else if (event?.name) {
      document.title = event.name;
    }
    return () => { document.title = ''; };
  }, [activePage?.title, event?.name]);

  // Push header info up to PublicPage shell whenever the active page or bp changes.
  // This lets the shell render HeaderSection in the global header slot.
  useEffect(() => {
    if (!setEventHeader) return;

    if (!activePage) {
      // Clear the shell header while loading or on error states
      setEventHeader(null);
      return;
    }

    const headerSection = activePage.sections?.find(s => s.type === 'header') || null;
    const headerConfig  = resolveHeaderConfig(activePage, eventLayout);
    setEventHeader({ headerSection, headerConfig, bp });

    // Clear on unmount so the shell doesn't keep showing the event header
    // after navigating away to a non-event page
    return () => setEventHeader(null);
  }, [activePage, bp, eventLayout, setEventHeader]);

  // Scroll to a section after cross-page navigation.
  // NavLink sets location.state.scrollToSection = sectionId when navigating
  // from a different page. We wait for the page to render before scrolling.
  useEffect(() => {
    const sectionId = location.state?.scrollToSection;
    if (!sectionId || pagesLoading) return;

    // Poll briefly to let the page render before scrolling
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(`section-${sectionId}`)
        || document.querySelector(`[data-section-id="${sectionId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Clear the state so a refresh doesn't re-scroll
        window.history.replaceState({ ...window.history.state, scrollToSection: undefined }, '');
        return;
      }
      if (++attempts < 20) setTimeout(tryScroll, 100);
    };
    setTimeout(tryScroll, 150);
  // eslint-disable-next-line 
  }, [location.state?.scrollToSection, pagesLoading]);

  if (!event || pagesLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!publishedPages || publishedPages.length === 0) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <Alert severity="info" sx={{ maxWidth: 480 }}>No published pages for this event yet.</Alert>
      </Box>
    );
  }

  if (!activePage) {
    return (
      <Box sx={{ bgcolor: branding.backgroundColor || '#F5F7FA', minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', p: 4 }}>
          <Alert severity="info" sx={{ maxWidth: 480 }}>This page is not available.</Alert>
        </Box>
      </Box>
    );
  }

  // Compute the spacer height so the first content section clears the header.
  // The header itself is now rendered in the shell (position: fixed/sticky at
  // the top level), so BuilderPreview needs to know how tall it is to offset.
  const headerConfig      = resolveHeaderConfig(activePage, eventLayout);
  const isPill            = (headerConfig?.template || 'classic') === 'pill';
  const stickyHeaderHeight = isPill
    ? (headerConfig?.style?.pillTop ?? 16) + 56 + 8
    : headerConfig?.style?.height || 72;

  return (
    <Box sx={{ bgcolor: branding.backgroundColor || '#F5F7FA', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <BuilderPreview
        page={activePage}
        eventLayout={eventLayout}
        bp={bp}
        stickyHeaderHeight={stickyHeaderHeight}
      />
    </Box>
  );
};

export default EventPage;